import { TimeEntryForm } from "../../types/times-module-types";
import { getEmptyTimeEntry } from "../../utils/timesUtils";
import { getDateFromISOFormat } from "../../../SharedModule/utils/dateUtils";

// ---------------------------------------------------------------------------
// computeEntryListAfterLoad — loadTimesInfo's entryList/entryListInvalid
// mutation sequence (ASUITE11-4412, S3776). A 1:1 translation: each branch
// fires in the same order against the same conditions, so a later branch can
// still overwrite an earlier one's result exactly as before (e.g. hasContent
// && entryListInvalid.length > 0 still ends with entryListInvalid's content,
// the empty-array assignment from the first branch discarded).
// ---------------------------------------------------------------------------

export type EntryListAfterLoadResult = {
  entryList: TimeEntryForm[];
  entryListInvalid: TimeEntryForm[];
  isSubmitted?: boolean;
};

export const computeEntryListAfterLoad = (
  entryList: TimeEntryForm[],
  entryListInvalid: TimeEntryForm[],
  hasPageContent: boolean,
  pageDate: Date,
): EntryListAfterLoadResult => {
  let nextEntryList = entryList;
  let nextEntryListInvalid = entryListInvalid;
  let isSubmitted: boolean | undefined;

  // if page has content, do not show time entries
  if (hasPageContent) {
    nextEntryList = [];
  }

  if (entryListInvalid.length > 0) {
    // add invalid entries and then clean array
    nextEntryList = entryListInvalid;
    nextEntryListInvalid = [];
    isSubmitted = true;
  }

  // complete to 5 entry list
  if (!hasPageContent) {
    nextEntryList = [...nextEntryList];
    for (let total = nextEntryList.length; total < 5; total++) {
      nextEntryList.push({
        ...getEmptyTimeEntry(pageDate),
        entryId: getEmptyTimeEntry(pageDate).entryId,
      });
    }
  }

  return { entryList: nextEntryList, entryListInvalid: nextEntryListInvalid, isSubmitted };
};

// ---------------------------------------------------------------------------
// computeTimeEntriesSheetViewWithFixups — loadTimesInfo's sheetView
// post-processing (ASUITE11-4412, S3776). A 1:1 translation, including the
// original's quirk of only fixing dates on sheetView[0] (not every element)
// — preserved as characterization, not a fix.
// ---------------------------------------------------------------------------

export const computeTimeEntriesSheetViewWithFixups = (
  sheetView: any[],
): any[] => {
  // sheet view doesn't have projectStatus because is the same as entityStatus (diff in list view)
  // add this propert to help function in timeUtils.ts => iconDeleteEnabled
  const result = sheetView.map((element) => ({
    ...element,
    projectStatus: element.entityStatus,
  }));

  // add this logic, when opens modal to add/edit entry in sheet view get corrects date
  if (result.length > 0) {
    const first = { ...result[0] };
    for (const key in first.dailyDetails) {
      if (first.dailyDetails[key].length > 0) {
        first.dailyDetails = {
          ...first.dailyDetails,
          [key]: first.dailyDetails[key].map((element) => ({
            ...element,
            timeEntries: element.timeEntries.map((timeEntry) => ({
              ...timeEntry,
              entryDate: getDateFromISOFormat(timeEntry.entryDate),
            })),
          })),
        };
      }
    }
    result[0] = first;
  }

  return result;
};
